import { useEffect, useState } from "react";
import {
  DiscoveryApi,
  discoveryApiRef,
  FetchApi,
  fetchApiRef,
  useApi,
} from "@backstage/core-plugin-api";

/** What a request to the front-door backend needs. */
export interface FrontDoorApis {
  discoveryApi: Pick<DiscoveryApi, "getBaseUrl">;
  fetchApi: Pick<FetchApi, "fetch">;
}

/** GETs `path` on the front-door backend. Rejects on a network error or a non-2xx status. */
export async function getFrontDoorJson<T>(
  apis: FrontDoorApis,
  path: string,
  label: string,
  signal?: AbortSignal
): Promise<T> {
  const baseUrl = await apis.discoveryApi.getBaseUrl("front-door");
  const response = await apis.fetchApi.fetch(`${baseUrl}${path}`, { signal });
  if (!response.ok) {
    throw new Error(`${label} request failed with status ${response.status}`);
  }
  return (await response.json()) as T;
}

/** `path` read once and parsed; undefined until read, and when it can't be. */
export function useFrontDoorJson<B, T>(
  path: string,
  label: string,
  parse: (body: B) => T
): T | undefined {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [value, setValue] = useState<T>();
  useEffect(() => {
    const controller = new AbortController();
    getFrontDoorJson<B>(
      { discoveryApi, fetchApi },
      path,
      label,
      controller.signal
    ).then(
      (body) => setValue(parse(body)),
      // Callers fall back to what they can show without it.
      () => undefined
    );
    return () => controller.abort();
    // `parse` is a module-level function in every caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryApi, fetchApi, path, label]);
  return value;
}
